import React, { createRef, useEffect, useState } from 'react'
import { Typography, Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AdditionalComponentCategoryCard, AdditionalComponentPowerMeterCategoryCard, ShopMainSkeleton, Element, Components } from 'src/Libs/shop'
import useSelectedUserRoutes from 'src/Hooks/useSelectedUserRoutes'
import { scrollToCategory } from 'src/Libs/shop/utils'
import useShopActiveCategory from 'src/Libs/shop/hooks/use-shop-category-scroll'
import { useHeaderTabs } from '@e3dc-react/shell/Libs/Header'
import { Product as SmartMeterProduct } from 'src/Libs/shop/models/product.model'
import { usePageActionsContext } from 'src/Libs/header/context'

interface SelectedConfiguredProduct {
  id: string
  qty: number
}

interface AdditionalComponentProps {
  title: string
  categories: Components[]
  isLoading: boolean
  onBack: () => void
  currentTransformers: Element[]
  availableSmartMeters: SmartMeterProduct[] | null
  showPowerMeterSection: boolean
  powerMeterTabLabel: string
  powerMeterInitialId: number
  onMoreInfo: (productId: string) => void
  isProductSelected: (productId: string) => boolean
  getSelectedProduct: (productId: string) => SelectedConfiguredProduct | undefined
  onDecreaseProductQuantity: (categoryId: string, selectedProductId: string, selectedProductQty: number) => void
  onIncreaseProductQuantity: (categoryId: string, selectedProductId: string) => void
  onAddProductToCart: (categoryId: string, productId: string, minPurchase: number, productHint: string | false) => void
  onRemoveProductFromCart: (categoryId: string, productId: string) => void
}

const useStyles = makeStyles()(theme => ({
  title: {
    fontSize: '32px',
    fontWeight: 400,
  },
  systemConfiguration: {
    marginBottom: '12px',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    [theme.breakpoints.down('md')]: {
      paddingTop: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
    },
  },
}))

/*
 *Additional Component shows categories, tabs, and power meter and smart meter section
 */
const AdditionalComponent: React.FC<AdditionalComponentProps> = ({
  title,
  categories,
  isLoading,
  onBack,
  currentTransformers,
  availableSmartMeters,
  showPowerMeterSection,
  powerMeterTabLabel,
  powerMeterInitialId,
  onMoreInfo,
  isProductSelected,
  getSelectedProduct,
  onDecreaseProductQuantity,
  onIncreaseProductQuantity,
  onAddProductToCart,
  onRemoveProductFromCart,
}) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { tabs, setTabs, setActiveTab } = useHeaderTabs()
  const [categoryRefs, setCategoryRefs] = useState<React.RefObject<HTMLDivElement>[]>([])
  const { activeCardIndex } = useShopActiveCategory(categoryRefs)
  const { dispatchPageActions } = usePageActionsContext()

  useEffect(() => {
    dispatchPageActions?.([
      {
        position: 'left',
        type: 'back',
        onClick: onBack,
      },
    ])

    return () => {
      dispatchPageActions?.([])
    }
  }, [dispatchPageActions, onBack])

  useSelectedUserRoutes(t('shop.system-configure.additional-component-title'))

  const createCategoryRefsAndTabs = (components: Components[] | undefined): void => {
    const newCategoryRefs = components?.map(() => createRef<HTMLDivElement>()) || []

    setCategoryRefs(newCategoryRefs)

    const powerMeterTab = {
      label: powerMeterTabLabel,
      onClick: () => {
        setActiveTab(0)
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        })
      },
    }

    const newTabs = [
      ...(showPowerMeterSection ? [powerMeterTab] : []),
      ...(components?.map((category, index) => ({
        label: category.translated.name,
        onClick: () => {
          setActiveTab(index + powerMeterInitialId)
          scrollToCategory(newCategoryRefs[index])
        },
      })) || []),
    ]

    setTabs(newTabs || [])
  }

  useEffect(() => {
    createCategoryRefsAndTabs(categories)

    return () => {
      setTabs([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, showPowerMeterSection, powerMeterInitialId, powerMeterTabLabel])

  useEffect(() => {
    if (tabs.length > 0) {
      if (activeCardIndex === null) {
        setActiveTab(0)
      } else {
        setActiveTab(activeCardIndex + powerMeterInitialId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCardIndex, categoryRefs])

  if (isLoading) {
    return (
      <Box marginTop={3}>
        <ShopMainSkeleton shopSkeletonType="product" />
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', marginRight: 0, marginBottom: '58px' }}>
      <Typography className={classes.title}>{title}</Typography>

      <Box className={classes.systemConfiguration}>
        {showPowerMeterSection && (
          <AdditionalComponentPowerMeterCategoryCard productData={currentTransformers} availableSmartMeters={availableSmartMeters} />
        )}

        {categories.map((category, index) => {
          return (
            <Box key={category.id} ref={categoryRefs[index]}>
              <AdditionalComponentCategoryCard
                key={category.id}
                categoryData={category}
                onMoreInfo={onMoreInfo}
                isProductSelected={isProductSelected}
                getSelectedProduct={getSelectedProduct}
                onDecreaseProductQuantity={onDecreaseProductQuantity}
                onIncreaseProductQuantity={onIncreaseProductQuantity}
                onAddProductToCart={onAddProductToCart}
                onRemoveProductFromCart={onRemoveProductFromCart}
              />
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default AdditionalComponent
